import { Link } from 'react-router-dom';
import {
  Facebook,
  Linkedin,
  Instagram
} from 'lucide-react';
import { getFooterLinks, getSocialLinks } from '@/data/mockNavigation';

export default function LandingFooter() {
  // Get navigation data from mock API
  const footerLinks = getFooterLinks();
  const socialLinksData = getSocialLinks();
  // Map social links with their icons
  const iconMap: Record<string, typeof Facebook> = {
    Facebook,
    LinkedIn: Linkedin,
    Instagram,
  };
  const socialLinks = socialLinksData.map(link => ({
    ...link,
    icon: iconMap[link.label] || Facebook,
  }));

  // Helper component to render links with proper routing
  const FooterLink = ({ link }: { link: typeof footerLinks.product[0] }) => {
    if (link.href) {
      return (
        <a
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-400 hover:text-white transition-colors"
        >
          {link.label}
        </a>
      );
    }

    return (
      <Link
        to={link.path || '#'}
        className="text-gray-400 hover:text-white transition-colors"
      >
        {link.label}
      </Link>
    );
  };

  return (
    <footer className="bg-tutara-charcoal dark:bg-tutara-dark-mode-surface border-t border-gray-800">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 lg:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <Link to="/" className="inline-block mb-4">
                <img 
                  src="/logos/logo-white.png" 
                  alt="Tutara" 
                  className="h-12 w-auto"
                />
              </Link>
              <p className="text-gray-400 mb-6 max-w-sm">
                AI-powered personalized learning platform that helps you learn anything 10x faster with tailored content and smart analogies.
              </p>
              
              {/* Social Links */}
              <div className="flex space-x-4">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      className="w-10 h-10 rounded-full bg-gray-800 dark:bg-tutara-dark-mode-bg flex items-center justify-center hover:bg-tutara-primary transition-colors"
                    >
                      <Icon className="h-5 w-5 text-gray-400 hover:text-white" />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Footer Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-3">
                {footerLinks.product.map((link) => (
                  <li key={link.label}>
                    <FooterLink link={link} />
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-3">
                {footerLinks.company.map((link) => (
                  <li key={link.label}>
                    <FooterLink link={link} />
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Resources</h3>
              <ul className="space-y-3">
                {footerLinks.resources.map((link) => (
                  <li key={link.label}>
                    <FooterLink link={link} />
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-3">
                {footerLinks.legal.map((link) => (
                  <li key={link.label}>
                    <FooterLink link={link} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="py-6 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Tutara. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">
                Terms
              </Link>
              <Link to="/cookies" className="text-gray-400 hover:text-white text-sm transition-colors">
                Cookies
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}